'use client';

import {
  Country,
  CountryLabel,
  setCountryCookie,
  setCurrencyCookie,
  useRegionCurrency,
  getLanguageCookie,
  setLanguageCookie,
  CLASSNAME,
} from '@skin-seoul/service';
import { CountryCurrency } from '@skin-seoul/service';
import {
  Button,
  createListCollection,
  Dialog,
  VStack,
  Select,
  useSelectContext,
  Flex,
} from '@skin-seoul/design-system';
import { useRef, useState } from 'react';
import { LucideIcon } from '../LucideIcon';

import { getObjectValues } from '@skin-seoul/utils';
import TranslateLanguageSelect from '../TranslateLanguageSelect/TranslateLanguageSelect';

type CountryChangeModalProps = {
  initialCountry: Country;
  disableReload?: boolean;
  isOpen: boolean;
  onClose: () => void;
};

const SelectValue = () => {
  const select = useSelectContext();
  const items = select.selectedItems as Array<{ label: string; value: string }>;
  const { label, value } = items[0];
  return (
    <Select.ValueText placeholder="Select member" textStyle="paragraph.small" color="neutral.900" overflowY="visible">
      <Flex w="fit-content" gap={1}>
        {label} <Flex w="fit-content">({CountryCurrency[value as Country]})</Flex>
      </Flex>
    </Select.ValueText>
  );
};

export const CountryChangeModal = ({
  initialCountry,
  isOpen,
  onClose,
  disableReload = false,
}: CountryChangeModalProps) => {
  const [selectedCountry, setSelectedCountry] = useState<Country[]>([initialCountry]);
  const [selectedLanguage, setSelectedLanguage] = useState<string | undefined>(getLanguageCookie());
  const shippingCountry = getObjectValues(Country);
  const { setCountry } = useRegionCurrency();
  const countryInputRef = useRef<HTMLButtonElement>(null);
  const countryItems = shippingCountry.map((country) => ({
    label: CountryLabel[country],
    value: country,
  }));
  const sortedCountryItems = countryItems.sort((a, b) => a.label.localeCompare(b.label));

  const isButtonActive = selectedCountry[0] !== initialCountry || selectedLanguage !== getLanguageCookie();

  const countryCollection = createListCollection({
    items: sortedCountryItems,
  });

  const handleOnSave = (value: Country[]) => {
    setCountryCookie(value[0]);
    setCurrencyCookie(CountryCurrency[value[0]]);
    setCountry(value[0]);
    if (selectedLanguage) {
      setLanguageCookie(selectedLanguage);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ss:user-language-change', { detail: selectedLanguage }));
      }
    }
    onClose();
    if (!disableReload) {
      window.location.reload();
    }
  };

  return (
    <Dialog.Root open={isOpen} motionPreset="scale" initialFocusEl={() => countryInputRef.current}>
      <Dialog.Backdrop backgroundColor="generic.100/20" backdropFilter="blur(2px)" />
      <Dialog.Positioner>
        <Dialog.Content
          borderRadius="4px"
          marginX={5}
          maxWidth={{
            base: '90%',
            mobile: '600px',
          }}
        >
          <Dialog.Header
            backgroundColor="primary.600"
            display="flex"
            flexDirection="row"
            paddingLeft={4}
            paddingRight={2}
            paddingY={2}
            alignItems="center"
            borderTopRadius="4px"
            justifyContent="space-between"
          >
            <Dialog.Title textStyle="paragraph.medium" fontWeight="medium" color="generic.0">
              Choose Your Location
            </Dialog.Title>
            <Button
              variant="ghost"
              onClick={onClose}
              _hover={{
                backgroundColor: 'transparent',
              }}
              maxWidth="40px"
              _icon={{
                color: 'white',
              }}
              size="md"
            >
              <LucideIcon name="X" />
            </Button>
          </Dialog.Header>
          <Dialog.Body paddingX="16px" paddingY="20px">
            <VStack gap={3} w="100%" h="100%">
              <Select.Root
                multiple={false}
                collection={countryCollection}
                deselectable={false}
                value={selectedCountry}
                onValueChange={(value) => {
                  setSelectedCountry(value.value as Country[]);
                }}
                positioning={{
                  sameWidth: true,
                  offset: { mainAxis: 0 },
                }}
              >
                <Select.HiddenSelect />
                <Select.Label textStyle="paragraph.small" color="neutral.900">
                  Shipping Country and Currency
                </Select.Label>
                <Select.Control>
                  <Select.Trigger
                    ref={countryInputRef}
                    border="1px solid"
                    borderColor="neutral.900"
                    _hover={{
                      borderColor: 'neutral.300',
                    }}
                    className={CLASSNAME.NOT_TRANSLATE}
                  >
                    <SelectValue />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.Indicator color="neutral.900" />
                  </Select.IndicatorGroup>
                </Select.Control>

                <Select.Positioner>
                  <Select.Content
                    maxHeight="30dvh"
                    boxShadow="0px 0px 10px 0px rgba(0, 0, 0, 0.1)"
                    className={CLASSNAME.NOT_TRANSLATE}
                  >
                    {countryItems.map((country) => (
                      <Select.Item
                        key={country.value}
                        item={country}
                        _selected={{
                          backgroundColor: 'neutral.100/60',
                        }}
                        w="100%"
                      >
                        <Flex w="fit-content" gap={1}>
                          {country.label} <Flex w="fit-content">({CountryCurrency[country.value]})</Flex>
                        </Flex>
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>
              <TranslateLanguageSelect
                value={selectedLanguage}
                onChange={setSelectedLanguage}
                applyImmediately={false}
              />
            </VStack>
          </Dialog.Body>
          <Dialog.Footer>
            <Button
              variant="outline"
              size="sm"
              color="neutral.900"
              border="1px solid"
              borderColor="neutral.200"
              _hover={{
                backgroundColor: 'neutral.100/60',
              }}
              textStyle="paragraph.small"
              onClick={onClose}
            >
              Cancel
            </Button>

            <Button
              variant="primary"
              size="sm"
              textStyle="paragraph.small"
              backgroundColor="neutral.900"
              _hover={{
                backgroundColor: 'neutral.800',
              }}
              disabled={!isButtonActive}
              onClick={() => handleOnSave(selectedCountry)}
            >
              Save
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};
